import { fullLabel } from "../components/WhereItIs";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErrorMessage } from "@/lib/api";
import { ScanField } from "@/components/ScanField";
import { AuthImg } from "@/components/AuthImg";
import { Button } from "@/components/ui/button";
import { scanSoundOnly, speak } from "@/lib/scanFeedback";
import { useFlash } from "@/floor/useFlash";
import { useGoDesk } from "@/lib/floorReturn";
import { setLeaveGuard, clearLeaveGuard } from "@/floor/leaveGuard";
import { prov, provTap } from "@/lib/provenance";
import { RackLevel } from "@/components/places/RackLevel";
import { ArrowLeft, MapPin, Check, Undo2, ChevronRight, Loader2, AlertTriangle, CheckCheck, Boxes, ImageOff } from "lucide-react";

// THE FLOOR PUT-AWAY SCREEN — IN HIS ORDER, which is how his warehouse actually works:
//   1. SCAN WHERE IT'S GOING — a shelf, a spot, or a container. Shown big at the top the whole
//      time ("PUTTING INTO: ROW 4 · SHELF B")
//   2. SCAN WHAT'S GOING THERE — a part, or a tote / pallet / crate — one after another, no taps
//      between them. A container takes everything inside it with it
//   3. DONE — one big button finishes that location before the next one
//
// ONE OPERATION TO PUT ANYTHING AWAY, his instruction 2026-10-08: *"Anything that can physically
// be picked up or driven can be moved: a part, a tote, a bin, a pallet, a crate. Only FIXED places
// can never be moved: shelves, spots, zones — anything built into the warehouse."* He hit the old
// split himself — parts onto a pallet here, then a DIFFERENT screen to move the pallet onto the
// shelf. The server decides which of the three a scan is, from the record.
//
// His correction before any of this shipped: *"That's how the old screen worked, and that part
// worked GREAT. The only problem was the clutter above it that made you scroll. I never asked for
// the flow to change — I asked for the noise to go."* So the banner and the scan box are PINNED
// and the list scrolls under them.
//
// JUDGED AGAINST HIS RULE FOR EVERY FLOOR SCREEN — fast, simple, can't mess it up from a tablet:
//  · ONE scan box exists, ever. Its meaning changes with the step.
//  · The TYPE check is on the SERVER (`GET /putaway/resolve-scan`), so the guardrail does not
//    depend on this file: a part where a place is expected is refused, and a FIXED place where an
//    item is expected is refused.
//  · A different FIXED place scanned mid-run is REFUSED, not auto-applied — his reason: *"bin
//    labels sit right next to parts in a rack... a stray scan of the bin label next door would
//    silently send every part after it into the wrong bin."* Switching is always deliberate.
//  · An empty container still moves and is NEVER blocked — amber on screen, carry on scanning;
//    the desk is only flagged if that container held parts recently.
//  · A re-read of a tag already in that bin is AMBER "already here" — never an error, never
//    recorded twice.
//  · UNDO LAST names the thing each time (part or container) and is repeatable back through the
//    run, but only within the loaded location: once DONE is tapped, the run is finished.
//  · IDLE CLEAR (his safety point): a loaded location drops itself after the owner-set idle time
//    so the next person can never inherit it. Nothing is lost — every scan is recorded as it
//    happens — and the banner counts down in amber for the last 30 seconds.
//  · Nothing destructive is rendered: no archive, delete, price or status change. No trucks —
//    placing a truck has a confirm step for a reason and belongs on the truck's own quick view.
//  · TWO BUTTONS FINISH A LOCATION (his spec, 2026-10-08): NEXT LOCATION goes back to "scan the
//    location" for the next one; DONE finishes and returns to the floor home screen. Trying to
//    leave ANY other way with a location loaded is stopped by a plain alert naming the location
//    and the count — nothing is lost either way, it just stops anyone forgetting.
//  · EVERY PART CONFIRMS FOUR WAYS: a beep, a full-screen green flash, a spoken "added", and the
//    part's own photo in the line, so they can see it is the right thing.
const WARN_AT_SECONDS = 30;

export default function FloorPutaway() {
  const navigate = useNavigate();
  const goDesk = useGoDesk();
  const [loc, setLoc] = useState(null);          // the loaded location, or null
  // THE LOADED LOCATION IS ALSO HELD IN A REF, and every scan handler reads it from there.
  // 2026-10-08: the camera's decode callback is registered with the driver once, so a handler
  // built from a stale render decided the STEP — his crew's bin "disappeared" after the first
  // scan and every part was sent to the server as a location. `ScanField` no longer freezes the
  // handler; this makes the step decision independent of any render at all.
  const locRef = useRef(null);
  const [rows, setRows] = useState([]);          // newest first
  const [busy, setBusy] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const [counts, setCounts] = useState(null);
  const [idleMinutes, setIdleMinutes] = useState(null);
  const [left, setLeft] = useState(null);        // seconds until the location clears
  const [leaveWarn, setLeaveWarn] = useState(false);   // "still loaded" alert
  const lastActivity = useRef(Date.now());
  const { trigger, FlashOverlay } = useFlash();

  const loadCounts = useCallback(() => {
    api.get("/putaway/queue/count").then(({ data }) => setCounts(data)).catch(() => {});
  }, []);
  useEffect(() => { loadCounts(); }, [loadCounts]);
  useEffect(() => {
    api.get("/putaway/settings").then(({ data }) => setIdleMinutes(data.idle_clear_minutes)).catch(() => {});
  }, []);

  const touch = useCallback(() => { lastActivity.current = Date.now(); setLeft(null); }, []);

  // ONE place where the loaded location changes — ref first, so a scan that lands in the same
  // tick as the state update still sees the truth.
  const loadLocation = useCallback((l) => { locRef.current = l || null; setLoc(l || null); }, []);

  // IDLE CLEAR — only ever runs while a location is loaded.
  useEffect(() => {
    if (!loc || !idleMinutes) return;
    const limit = idleMinutes * 60 * 1000;
    const t = setInterval(() => {
      const remaining = Math.ceil((limit - (Date.now() - lastActivity.current)) / 1000);
      if (remaining <= 0) {
        loadLocation(null); setRows([]); setLeft(null);
        trigger("warning", "LOCATION CLEARED — SCAN IT AGAIN");
        loadCounts();
      } else setLeft(remaining <= WARN_AT_SECONDS ? remaining : null);
    }, 1000);
    return () => clearInterval(t);
  }, [loc, idleMinutes, trigger, loadCounts]);

  const scanLocation = async (code) => {
    setBusy(true); touch();
    try {
      const { data } = await api.get("/putaway/resolve-scan", { params: { code, expect: "bin" } });
      if (!data.ok) { trigger("error", data.flash || "NOT A LOCATION"); return; }
      trigger("success", data.flash || data.location?.short_code || "LOCATION SET");
      loadLocation(data.location); setRows([]); touch();
    } catch (e) { trigger("error", (apiErrorMessage(e) || "NOT FOUND").toUpperCase()); }
    finally { setBusy(false); }
  };

  const scanItem = async (code, meta) => {
    const here = locRef.current;
    if (!here) return scanLocation(code);
    setBusy(true); touch();
    try {
      const { data } = await api.get("/putaway/resolve-scan", { params: { code, expect: "part" } });
      if (!data.ok) {
        if (data.kind === "location" && data.location?.id === here.id) {
          trigger("warning", "ALREADY PUTTING INTO THIS LOCATION");
        } else if (data.kind === "location" && data.stationary_container) {
          // A built-in rack (his vertical cantilever racks) is stored as a container, so it NAMES
          // itself here instead of reading like the bin label next door — it is not somewhere you
          // can move to, and it can never be moved by a scan.
          trigger("error", data.flash || "THAT'S A BUILT-IN RACK, NOT A PART");
        } else if (data.kind === "location") {
          trigger("error", `TAP NEXT LOCATION TO LEAVE ${fullLabel(here) || here.short_code}`);
        } else trigger("error", data.flash || "NOT A PART");
        return;
      }
      if (data.kind === "container") return moveContainer(data.container, here, meta);
      return putPart(data.part, here, meta);
    } catch (e) { trigger("error", (apiErrorMessage(e) || "COULD NOT RECORD").toUpperCase()); }
    finally { setBusy(false); touch(); }
  };

  const putPart = async (p, here, meta) => {
    const res = await api.post("/putaway", { part_scan: p.short_code, location_id: here.id,
                                             provenance: prov("floor_putaway", meta) });
    const r = res.data;
    if (r.already) {
      trigger("warning", `${r.part?.short_code} IS ALREADY HERE`);
      setRows((l) => [{ kind: "part", already: true, label: r.part?.short_code,
                        photo: r.part?.photo_file_id || null, ts: Date.now() }, ...l]);
      return;
    }
    if (!r.ok) { trigger("error", (r.message || "COULD NOT RECORD").toUpperCase()); return; }
    // A LOT FINISHES ITSELF WHEN ITS LAST PART LANDS ON A SHELF (his rule, 2026-10-14 late) —
    // said out loud at the moment it happens, not discovered later.
    if (r.lot_closed) { trigger("success", `${r.lot_closed.batch_number} FINISHED`); speak("added"); }
    else { trigger("success", r.part?.short_code); speak("added"); }
    setRows((l) => [{ kind: "part", ok: true, id: r.part.id, label: r.part?.short_code,
                      photo: r.part?.photo_file_id || null,
                      sub: r.lot_closed ? `${r.lot_closed.batch_number} is finished — that was the last part out of its bin`
                        : (r.from_location?.short_code ? `moved from ${r.from_location.short_code}` : (r.part?.name || "")),
                      lotClosed: r.lot_closed ? r.lot_closed.batch_number : null,
                      from: r.from_location_id || null, ts: Date.now() }, ...l]);
    loadCounts();
  };

  // A CONTAINER TAKES EVERYTHING INSIDE IT WITH IT (his rule: one operation to put anything away).
  // The pallet itself is what moves — no part inside is touched, so nothing can be left behind.
  const moveContainer = async (c, here, meta) => {
    if (c.id === here.id) { trigger("warning", "ALREADY PUTTING INTO THIS LOCATION"); return; }
    const res = await api.post("/containers/move", { container_scan: c.short_code, location_scan: here.short_code,
                                                     provenance: prov("floor_putaway", meta) });
    const r = res.data;
    if (!r.ok) { trigger("error", (r.message || "COULD NOT MOVE IT").toUpperCase()); return; }
    const empty = (r.moved_parts || 0) === 0;
    // Amber if nothing came with it — but it NEVER stops them. No confirm, no extra tap.
    if (empty) { trigger("warning", `${c.short_code} MOVED — 0 PARTS CAME WITH IT`); }
    else { trigger("success", `${c.short_code} · ${r.moved_parts} PARTS WITH IT`); }
    setRows((l) => [{ kind: "container", ok: true, warn: empty, id: c.id, label: c.short_code,
                      sub: empty ? "0 parts came with it — is this the right one?"
                                 : `${r.moved_parts} part${r.moved_parts === 1 ? "" : "s"} came with it · from ${c.was}`,
                      from: r.from_parent_id || null, ts: Date.now() }, ...l]);
    loadCounts();
  };

  const onScan = (code, meta) => (locRef.current ? scanItem(code, meta) : scanLocation(code));

  const lastUndoable = rows.find((r) => r.ok && !r.undone);

  const undoLast = async () => {
    if (!lastUndoable) return;
    setUndoing(true); touch();
    try {
      const { data } = await api.post("/move-item/undo", {
        kind: lastUndoable.kind, item_id: lastUndoable.id, previous_location_id: lastUndoable.from,
        provenance: provTap("floor_putaway"),
      });
      if (!data.ok) { trigger("error", (data.message || "COULD NOT UNDO").toUpperCase()); return; }
      trigger("warning", `${lastUndoable.label} TAKEN BACK OUT`);
      setRows((l) => l.map((r) => (r.ts === lastUndoable.ts ? { ...r, undone: true } : r)));
      loadCounts();
    } catch (e) { trigger("error", (apiErrorMessage(e) || "COULD NOT UNDO").toUpperCase()); }
    finally { setUndoing(false); touch(); }
  };

  const live = rows.filter((r) => r.ok && !r.undone);
  const putCount = live.filter((r) => r.kind === "part").length;
  const boxCount = live.filter((r) => r.kind === "container").length;

  // THE ALERT BEFORE LEAVING. While a location is loaded, every other way out of this screen —
  // the floor logo, the Desk/Floor toggle, the browser's back button, closing the tab — stops
  // and says what is still loaded and how many parts went in. Nothing is lost either way.
  useEffect(() => {
    if (!loc) { setLeaveWarn(false); return; }
    const warn = () => { scanSoundOnly(false); setLeaveWarn(true); };
    setLeaveGuard(warn);
    window.history.pushState({ fasPutawayLoaded: true }, "");
    const onPop = () => { window.history.pushState({ fasPutawayLoaded: true }, ""); warn(); };
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ""; return ""; };
    window.addEventListener("popstate", onPop);
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      clearLeaveGuard(warn);
      window.removeEventListener("popstate", onPop);
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [loc?.id]);   // eslint-disable-line react-hooks/exhaustive-deps
  const countLine = boxCount
    ? `${putCount} PART${putCount === 1 ? "" : "S"} · ${boxCount} CONTAINER${boxCount === 1 ? "" : "S"}`
    : `${putCount} PART${putCount === 1 ? "" : "S"}`;
  const doneLabel = boxCount
    ? `DONE · ${putCount + boxCount} ITEMS` : `DONE · ${putCount} PART${putCount === 1 ? "" : "S"}`;

  // DONE and NEXT LOCATION both record nothing — every scan was recorded the moment it beeped.
  // NEXT LOCATION finishes this location and waits for the next one. DONE finishes the run and
  // goes back to the floor home screen. His spec, 2026-10-08.
  const nextLocation = () => {
    trigger("success", `${putCount + boxCount} → ${loc.short_code}`);
    setLeaveWarn(false);
    loadLocation(null); setRows([]); setLeft(null); loadCounts();
  };
  const done = () => {
    trigger("success", `${putCount + boxCount} → ${loc.short_code}`);
    setLeaveWarn(false);
    loadLocation(null); setRows([]); setLeft(null); loadCounts();
    setTimeout(() => navigate("/floor"), 650);   // let the flash land before the screen changes
  };

  const noBin = counts?.no_bin_recorded ?? 0;
  const returns = counts?.returns_waiting ?? 0;

  return (
    <div className="flex-1 flex flex-col min-h-0 p-4 sm:p-6" data-testid="floor-putaway"
      onPointerDown={touch}>
      {FlashOverlay}

      {/* Back far left and nothing else. Nothing in the top corners to hit by accident.
          With a location loaded it asks first — the same alert as every other way out. */}
      <button type="button" data-testid="floor-putaway-back"
        onClick={() => (loc ? (scanSoundOnly(false), setLeaveWarn(true)) : navigate("/floor"))}
        className="flex items-center gap-1.5 font-bold text-[#B01E28] text-lg self-start h-12 shrink-0">
        <ArrowLeft className="w-6 h-6" /> {loc ? "Leave this location" : "Home"}
      </button>

      {/* ===== PUTTING INTO — pinned, biggest type on the page, there the whole time ===== */}
      {loc && (
        <div data-testid="floor-putaway-banner"
          className={`shrink-0 rounded-2xl border-2 p-4 ${left !== null
            ? "border-amber-500 bg-amber-50" : "border-[#E0781A] bg-[#FFF9F3]"}`}>
          <div className="text-xs font-black uppercase tracking-[0.2em] text-slate-500">Putting into</div>
          <div data-testid="floor-putaway-banner-location"
            className="font-display font-black text-3xl sm:text-4xl leading-tight text-slate-900 break-words">
            {fullLabel(loc) || loc.short_code}
          </div>
          <div className="font-mono text-sm text-slate-500">{loc.short_code}{loc.placed === false ? " · NOT PLACED ANYWHERE YET" : ""}</div>
          <RackLevel level={loc.rack_level} testid="floor-putaway-level" />
          {left !== null && (
            <div data-testid="floor-putaway-idle-warning"
              className="mt-2 flex items-center gap-2 text-base font-black text-amber-700">
              <AlertTriangle className="w-5 h-5" />CLEARING IN {left}s — SCAN OR TAP TO KEEP
            </div>
          )}
        </div>
      )}

      {/* ===== THE ONE SCAN BOX — pinned directly under the banner, never scrolls away ===== */}
      <div className="mt-4 shrink-0" data-testid={loc ? "floor-putaway-step-part" : "floor-putaway-step-location"}>
        <ScanField testid="floor-putaway-scan" big continuous
          label={loc ? "SCAN PART AFTER PART" : "STEP 1 — SCAN WHERE IT'S GOING"}
          placeholder={loc ? "Scan a part — or a tote / pallet / crate" : "Scan a shelf, spot or container"}
          submitLabel={loc ? "Put it in" : "Load it"}
          onScan={onScan} busy={busy} />
      </div>

      {loc ? (
        <>
          <div data-testid="floor-putaway-count"
            className="mt-4 shrink-0 font-display font-black text-xl text-slate-900">
            {countLine} IN THIS LOCATION
          </div>

          {/* The only thing that scrolls. */}
          <div data-testid="floor-putaway-list" className="mt-2 flex-1 min-h-0 overflow-y-auto">
            {rows.map((r) => (
              <div key={r.ts} data-testid="floor-putaway-row"
                className={`flex items-center gap-3 border-b border-slate-100 py-3 ${r.undone ? "opacity-50" : ""}`}>
                {r.already || r.warn
                  ? <AlertTriangle className="w-7 h-7 text-amber-500 shrink-0" />
                  : r.kind === "container"
                    ? <Boxes className={`w-7 h-7 shrink-0 ${r.undone ? "text-slate-400" : "text-emerald-600"}`} />
                    : <Check className={`w-7 h-7 shrink-0 ${r.undone ? "text-slate-400" : "text-emerald-600"}`} />}
                {/* THE PART'S OWN PHOTO, so they can SEE it is the right thing (his spec). */}
                {r.kind === "part" && (
                  r.photo
                    ? <div data-testid="floor-putaway-row-photo" className="w-16 h-16 shrink-0">
                        <AuthImg fileId={r.photo} thumb alt={r.label}
                          className="w-16 h-16 rounded-lg object-cover bg-slate-100" />
                      </div>
                    : <div data-testid="floor-putaway-row-nophoto"
                        className="w-16 h-16 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                        <ImageOff className="w-6 h-6 text-slate-300" />
                      </div>
                )}
                <div className="min-w-0">
                  <div className="font-mono font-black text-xl text-slate-900 truncate">{r.label}</div>
                  <div className={`text-sm truncate ${r.warn && !r.undone ? "text-amber-700 font-semibold" : r.lotClosed && !r.undone ? "text-emerald-700 font-bold" : "text-slate-500"}`}
                    data-testid={r.lotClosed ? "floor-putaway-row-lot-finished" : undefined}>
                    {r.undone ? "taken back out"
                      : r.already ? "already here — not counted twice"
                      : r.sub}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pinned at the bottom, far from the scan box, full width. TWO buttons finish a
              location: NEXT LOCATION waits for the next one, DONE goes back to the home screen. */}
          <div className="mt-4 shrink-0 flex flex-col">
            <div className="flex gap-3">
              <Button data-testid="floor-putaway-next-location" onClick={nextLocation}
                className="h-20 flex-1 min-w-0 px-2 whitespace-normal leading-tight rounded-xl text-base sm:text-2xl font-black bg-[#0F172A] hover:bg-black text-white">
                <ChevronRight className="w-8 h-8 mr-1 sm:mr-2 shrink-0" />NEXT LOCATION
              </Button>
              <Button data-testid="floor-putaway-done" onClick={done}
                className="h-20 flex-1 min-w-0 px-2 whitespace-normal leading-tight rounded-xl text-base sm:text-2xl font-black bg-emerald-700 hover:bg-emerald-800 text-white">
                <CheckCheck className="w-8 h-8 mr-1 sm:mr-2 shrink-0" />{doneLabel}
              </Button>
            </div>
            <div className="h-6" />
            <Button data-testid="floor-putaway-undo" variant="outline" onClick={undoLast}
              disabled={undoing || !lastUndoable}
              className="h-16 w-full rounded-xl text-lg font-bold border-2 border-slate-300 text-slate-600 disabled:opacity-40">
              {undoing ? <Loader2 className="w-6 h-6 mr-2 animate-spin" /> : <Undo2 className="w-6 h-6 mr-2" />}
              {lastUndoable ? `UNDO LAST — ${lastUndoable.label}` : "UNDO LAST"}
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className="flex-1" />
          {/* Between runs only — zero noise while they are scanning. */}
          {counts && (
            <button type="button" data-testid="floor-putaway-nobin-line"
              onClick={() => goDesk("/parts?dq=needs_putaway")}
              className="mt-6 h-14 w-full shrink-0 rounded-xl border border-slate-200 bg-white px-4 flex items-center gap-2 text-left">
              <MapPin className="w-5 h-5 text-slate-400 shrink-0" />
              <span className="text-base font-semibold text-slate-600 truncate">
                {returns > 0 ? `${returns} return${returns === 1 ? "" : "s"} · ` : ""}
                {noBin} part{noBin === 1 ? "" : "s"} with no bin recorded
              </span>
              <ChevronRight className="w-6 h-6 text-slate-300 ml-auto shrink-0" />
            </button>
          )}
        </>
      )}

      {/* ===== STILL LOADED — the alert before leaving. His words, 2026-10-08. ===== */}
      {leaveWarn && loc && (
        <div data-testid="floor-putaway-leave-warning"
          className="fixed inset-x-0 top-vv h-vv z-[90] bg-black/60 flex items-end sm:items-center justify-center p-4 overflow-y-auto">
          <div className="w-full max-w-xl rounded-2xl bg-white p-5 border-4 border-amber-500">
            <div className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="w-8 h-8 shrink-0" />
              <span className="font-black uppercase tracking-wider">Still loaded</span>
            </div>
            <div data-testid="floor-putaway-leave-warning-text"
              className="mt-2 font-display font-black text-2xl sm:text-3xl leading-tight text-slate-900">
              {(fullLabel(loc) || loc.short_code).toUpperCase()} STILL LOADED — {putCount + boxCount} {putCount + boxCount === 1 ? "ITEM" : "ITEMS"} PUT AWAY.
            </div>
            <div className="mt-1 text-base text-slate-600">
              Nothing is lost either way. Tap NEXT LOCATION or DONE.
            </div>
            <div className="mt-4 flex gap-3">
              <Button data-testid="floor-putaway-leave-next" onClick={nextLocation}
                className="h-20 flex-1 rounded-xl text-xl font-black bg-[#0F172A] hover:bg-black text-white">
                NEXT LOCATION
              </Button>
              <Button data-testid="floor-putaway-leave-done" onClick={done}
                className="h-20 flex-1 rounded-xl text-xl font-black bg-emerald-700 hover:bg-emerald-800 text-white">
                DONE
              </Button>
            </div>
            <Button data-testid="floor-putaway-leave-keep" variant="outline"
              onClick={() => { setLeaveWarn(false); touch(); }}
              className="mt-3 h-14 w-full rounded-xl text-base font-bold border-2 border-slate-300 text-slate-600">
              KEEP SCANNING
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
